/**
 * Creates a memoized function that caches its result.
 * The value is computed only once and then cached until cleared.
 * @util
 *
 * @template T - The type of the memoized value
 * @param factory - Function that computes the value to memoize
 * @returns A memoized function with utility methods
 *
 * @example
 * ```typescript
 * const expensive = createMemo(() => {
 *     console.log('Computing...');
 *     return 42;
 * });
 *
 * console.log(expensive()); // Logs "Computing..." and returns 42
 * console.log(expensive()); // Returns 42 (no computation)
 * expensive.clear();
 * console.log(expensive()); // Logs "Computing..." and returns 42
 * ```
 */
export function createMemo(factory) {
    let valueSet = false;
    let value;
    const memo = (() => {
        if (!valueSet) {
            value = factory();
            valueSet = true;
        }
        return value;
    });
    memo.clear = () => {
        value = undefined;
        valueSet = false;
    };
    memo.value = () => value;
    return memo;
}
/**
 * Creates an async memoized function that caches its result.
 * The value is computed only once and then cached until cleared.
 * @util
 *
 * @template T - The type of the memoized value
 * @param factory - Async function that computes the value to memoize
 * @returns An async memoized function with utility methods
 *
 * @example
 * ```typescript
 * const expensive = createMemoAsync(async () => {
 *     console.log('Computing...');
 *     await waitFor(1000);
 *     return 42;
 * });
 *
 * await expensive(); // Logs "Computing..." and returns 42 after 1s
 * await expensive(); // Returns 42 immediately
 * expensive.clear();
 * await expensive(); // Logs "Computing..." and returns 42 after 1s
 * ```
 */
export function createMemoAsync(factory) {
    let promise;
    let value;
    const memo = (() => {
        if (!promise) {
            promise = factory().then(result => {
                value = result;
                return result;
            });
        }
        return promise;
    });
    memo.clear = () => {
        promise = undefined;
        value = undefined;
    };
    memo.promise = () => promise;
    memo.value = () => value;
    return memo;
}
//# sourceMappingURL=createMemo.js.map